import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, get, setToken } from "@/lib/api";
import { StatusBar } from "@/components/bridge/StatusBar";
import { AuthBanner } from "@/components/bridge/AuthBanner";
import { IndicesStrip } from "@/components/bridge/IndicesStrip";
import { CommoditiesStrip } from "@/components/bridge/CommoditiesStrip";
import { SearchBar } from "@/components/bridge/SearchBar";
import { SnapshotTable } from "@/components/bridge/SnapshotTable";
import { SymbolSheet } from "@/components/bridge/SymbolSheet";
import { OptionChain } from "@/components/bridge/OptionChain";
import { GainersLosers } from "@/components/bridge/GainersLosers";
import { SubscriptionsPanel } from "@/components/bridge/SubscriptionsPanel";
import { AlertsPanel } from "@/components/bridge/AlertsPanel";
import { SettingsDialog } from "@/components/settings/SettingsDialog";
import { LiveProvider, useLiveRows } from "@/components/bridge/LiveProvider";

const TRIG = "font-mono text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-300";

function DashboardInner({ onLogout }) {
  const [sym, setSym] = useState(null);
  const [settings, setSettings] = useState(false);
  const [tab, setTab] = useState("watchlist");
  const { data: health, refresh: refreshHealth } = usePoll(() => get("/health"), 3000, []);
  const { data: auth } = usePoll(() => get("/auth/status"), 5000, []);
  const { data: idx } = usePoll(() => get("/indices/live"), 4000, []);
  const { data: wl } = usePoll(() => get("/watchlist/live"), 4000, []);
  const { data: com } = usePoll(() => get("/commodities/live"), 4000, []);
  const { data: od } = usePoll(() => (tab === "ondemand" ? get("/subscriptions", { reason: "on_demand", include_snapshot: true }) : Promise.resolve(null)), 4000, [tab]);

  const indices = useLiveRows(idx?.snapshots);
  const commodities = useLiveRows(com?.snapshots);
  const watchlist = useLiveRows(wl?.snapshots);
  const ondemand = useLiveRows((od?.items || []).map((i) => i.snapshot).filter(Boolean));

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("kite_login");
    if (r === "success") toast.success("Zerodha login successful — live session starting");
    if (r === "error") toast.error(`Zerodha login failed (${p.get("reason") || "unknown"})`);
    if (r) window.history.replaceState({}, "", "/");
  }, []);

  const logout = () => { setToken(null); onLogout(); };
  const setDataMode = async (allow) => {
    try {
      await api.post("/data-mode", { allow_simulated: allow });
      toast.success(allow ? "Simulated mode — showing simulated data only" : "Live mode — showing live Kite data only");
      refreshHealth();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const liveOnlyBlank = health?.auth_state === "live_only";
  return (
    <div className="relative z-10 min-h-screen" data-testid="dashboard">
      <StatusBar health={health} auth={auth} onSettings={() => setSettings(true)} onLogout={logout} onDataMode={setDataMode} />
      <main className="mx-auto w-full max-w-[1720px] space-y-4 p-3 sm:p-4 lg:p-6">
        <AuthBanner health={health} auth={auth} onSettings={() => setSettings(true)} />
        {!liveOnlyBlank && <IndicesStrip rows={idx?.snapshots ? indices : null} onOpen={setSym} />}
        {!liveOnlyBlank && <CommoditiesStrip rows={com?.snapshots ? commodities : null} onOpen={setSym} />}
        <SearchBar onOpen={setSym} />
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
          <section className="overflow-hidden rounded-md border border-slate-800 bg-[#111827] lg:col-span-8">
            <Tabs value={tab} onValueChange={setTab}>
              <div className="scroll-thin flex items-center justify-between overflow-x-auto border-b border-slate-800 px-3 py-2">
                <TabsList className="shrink-0 bg-slate-900">
                  <TabsTrigger data-testid="tab-watchlist" value="watchlist" className={TRIG}>Watchlist</TabsTrigger>
                  <TabsTrigger data-testid="tab-ondemand" value="ondemand" className={TRIG}>On-demand</TabsTrigger>
                  <TabsTrigger data-testid="tab-optionchain" value="optionchain" className={TRIG}>Option chain</TabsTrigger>
                  <TabsTrigger data-testid="tab-subscriptions" value="subscriptions" className={TRIG}>Subscriptions</TabsTrigger>
                </TabsList>
                <span className="hidden font-mono text-[10px] text-slate-600 md:inline">Live push · current + previous tick only</span>
              </div>
              <TabsContent value="watchlist" className="m-0">
                <SnapshotTable rows={wl?.snapshots ? watchlist : null} onOpen={setSym} testid="watchlist-table" showDerivs={false}
                  empty={liveOnlyBlank ? "Live-only mode: no data until Zerodha is connected." : "No symbols in watchlist."} />
              </TabsContent>
              <TabsContent value="ondemand" className="m-0">
                <SnapshotTable rows={ondemand} onOpen={setSym} testid="ondemand-table"
                  empty={liveOnlyBlank ? "Live-only mode: connect Zerodha to subscribe symbols." : "Nothing subscribed on demand. Search a symbol and open it or press SUB."} />
              </TabsContent>
              <TabsContent value="optionchain" className="m-0"><OptionChain onOpen={setSym} /></TabsContent>
              <TabsContent value="subscriptions" className="m-0"><SubscriptionsPanel onOpen={setSym} /></TabsContent>
            </Tabs>
          </section>
          <aside className="space-y-4 lg:col-span-4">
            <GainersLosers onOpen={setSym} />
            <AlertsPanel />
          </aside>
        </div>
      </main>
      <SymbolSheet sym={sym} onClose={() => setSym(null)} />
      <SettingsDialog open={settings} onOpenChange={setSettings} />
    </div>
  );
}

export default function Dashboard(props) {
  return (
    <LiveProvider>
      <DashboardInner {...props} />
    </LiveProvider>
  );
}
